/** @jsxImportSource @emotion/react */
import { faCheckCircle, faPaperPlane } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Alert, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";
import { useLayerChildren, useRouteLayer } from "../../../../hooks/useCurrentEntities";
import { getFilledArrayOrDefault } from "../../../../utils";
import { toast } from "../../../../utils/toast";
import { callAvosApi } from "../../../../utils/useAvosApiHook";
import { Button } from "../../../Helper/Button";
import { NO_FACILITY } from "../fields/LayerMetaAtCurrentFacilityField";

// Order meta naming the order's flow ("inbound" / "outbound"). Sent and closed
// state is kept per flow and facility on the pallet:
//   sent_<flow>   = facility where AVOS sent the status to the external system
//   closed_<flow> = facility where the external system (D365) confirmed closure
// A pallet only counts as sent/closed for this order while it is still in
// that facility, so moving it reopens it. Mirrors
// backend/services/integration/mission_us_data_ingestion_service.py.
export const ORDER_FLOW_META_KEY = "flow";

// Mirrors MAX_PALLETS_PER_STATUS_SEND in backend/api/web/layers/layers.py.
// The backend rejects larger requests with a 400, so stop the user here first.
const MAX_PALLETS_PER_SEND = 100;

interface PalletRow {
    id: string;
    label: string;
    wasSent: boolean;
    isClosed: boolean;
}

/**
 * Read a meta value off a child layer.
 *
 * The children endpoint flattens meta onto the layer object, but some call
 * sites still receive the raw ``meta`` array, so support both shapes.
 */
const getMetaValue = (child: any, key: string): string | undefined => {
    if (child?.[key] !== undefined && child?.[key] !== null) {
        return String(child[key]);
    }
    return child?.meta?.find((m) => m.key === key)?.value;
};

const isStampedAtCurrentFacility = (child: any, key: string): boolean => {
    const stamped = getMetaValue(child, key);
    return !!stamped && stamped === (child?.latest_location?.facility || NO_FACILITY);
};

export default function SendPalletsStatusToTenantModal() {
    const params = useParams();
    const tenantId = params.tenant_id as string;

    const layer = useRouteLayer();
    const { data: children, refetch: refetchChildren } = useLayerChildren(tenantId, layer.id);
    const tenantName = useSelector((state: any) => state.auth.user?.tenant?.tenant_name);
    const flow = getMetaValue(layer, ORDER_FLOW_META_KEY);

    const [isOpen, setIsOpen] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);
    const [result, setResult] = useState<any>(null);

    const pallets: PalletRow[] = useMemo(() => getFilledArrayOrDefault(children?.results)
        // An order has batch children next to its pallet children, and the D365
        // message is about a pallet. Sending a batch would put a batch label in
        // the "Pallet" field, so never offer them here.
        .filter((child: any) => child.type === "pallet")
        .map((child: any) => ({
            id: child.id,
            label: child.label,
            // An order without a flow has no recorded sends or closures.
            wasSent: !!flow && isStampedAtCurrentFacility(child, `sent_${flow}`),
            isClosed: !!flow && isStampedAtCurrentFacility(child, `closed_${flow}`),
        })), [children, flow]);

    // A closed pallet has already been dealt with on the D365 side, so
    // re-reporting it is at best a no-op. Sending is still allowed (a user may
    // deliberately re-send), it is just not offered by default.
    const sendableIds = useMemo(
        () => pallets.filter((p) => !p.wasSent && !p.isClosed).map((p) => p.id),
        [pallets],
    );

    // The children may still be loading when the modal is opened, so seed the
    // default selection from an effect rather than from the open handler —
    // otherwise opening early leaves the dialog with nothing selected.
    const hasSeededSelection = useRef(false);
    useEffect(() => {
        if (!isOpen) {
            hasSeededSelection.current = false;
            return;
        }
        if (hasSeededSelection.current || pallets.length === 0) {
            return;
        }
        hasSeededSelection.current = true;
        // Default to everything that is still outstanding for this order —
        // not yet reported, and not already closed by D365.
        setSelected(sendableIds);
    }, [isOpen, pallets, sendableIds]);

    const open = () => {
        setSelected([]);
        setResult(null);
        setIsOpen(true);
    };

    const close = () => {
        setIsOpen(false);
        setIsSending(false);
    };

    const toggle = (id: string) => {
        setSelected((current) => (current.includes(id)
            ? current.filter((i) => i !== id)
            : [...current, id]));
    };

    const allSelected = pallets.length > 0 && selected.length === pallets.length;

    const toggleAll = () => {
        setSelected(allSelected ? [] : pallets.map((p) => p.id));
    };

    const handleSend = async () => {
        setIsSending(true);
        setResult(null);
        try {
            const response = await callAvosApi(`/layers/${layer.id}/send-status-to-tenant`, {
                method: "POST",
                data: { layer_ids: selected },
            });

            const data = response?.data ?? {};
            setResult(data);

            const sentCount = getFilledArrayOrDefault(data.sent).length;
            const failedCount = getFilledArrayOrDefault(data.failed).length;

            if (sentCount > 0) {
                toast.success(`${sentCount} pallet status${sentCount === 1 ? "" : "es"} sent to ${tenantName}.`);
            }
            if (failedCount > 0) {
                toast.error(`${failedCount} pallet${failedCount === 1 ? "" : "s"} could not be sent.`);
            }

            await refetchChildren();
        } catch {
            // Error toast already shown by the axios interceptor.
        } finally {
            setIsSending(false);
        }
    };

    if (!layer?.id) {
        return null;
    }

    const tooMany = selected.length > MAX_PALLETS_PER_SEND;
    const failedRows = getFilledArrayOrDefault(result?.failed);
    const skippedRows = getFilledArrayOrDefault(result?.skipped);

    return (
        <>
            <Button
                color="primary"
                onClick={open}
                className="text-capitalize flex-grow-1"
                icon={faPaperPlane}
            >
                Send status to {tenantName}
            </Button>

            <Modal isOpen={isOpen} toggle={close} size="lg">
                <ModalHeader toggle={close}>
                    Send pallet status to {tenantName}
                </ModalHeader>

                <ModalBody>
                    {pallets.length === 0 ? (
                        <Alert color="info" className="mb-0">
                            This order has no pallets to send.
                        </Alert>
                    ) : (
                        <>
                            <p className="text-muted">
                                Select the pallets to report for <strong>{layer.label}</strong>.
                                A pallet does not need its own check to be sent.
                            </p>

                            {tooMany && (
                                <Alert color="warning">
                                    You can send at most {MAX_PALLETS_PER_SEND} pallets at a time.
                                    Currently selected: {selected.length}.
                                </Alert>
                            )}

                            <Table size="sm" responsive className="align-middle">
                                <thead>
                                    <tr>
                                        <th css={{ width: "3rem" }}>
                                            <Input
                                                type="checkbox"
                                                checked={allSelected}
                                                onChange={toggleAll}
                                                aria-label="Select all pallets"
                                            />
                                        </th>
                                        <th>Pallet</th>
                                        <th>Status</th>
                                        <th>Open/Closed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pallets.map((pallet) => (
                                        <tr key={pallet.id}>
                                            <td>
                                                <Input
                                                    type="checkbox"
                                                    id={`send-status-${pallet.id}`}
                                                    checked={selected.includes(pallet.id)}
                                                    onChange={() => toggle(pallet.id)}
                                                />
                                            </td>
                                            <td>
                                                <Label
                                                    for={`send-status-${pallet.id}`}
                                                    className="mb-0"
                                                    css={{ cursor: "pointer" }}
                                                >
                                                    {pallet.label}
                                                </Label>
                                            </td>
                                            <td>
                                                {pallet.wasSent ? (
                                                    <span className="text-success">
                                                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                                        Sent
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">Not sent</span>
                                                )}
                                            </td>
                                            <td>
                                                {pallet.isClosed ? (
                                                    <span className="text-success">
                                                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                                        Closed
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">Open</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {failedRows.length > 0 && (
                                <Alert color="danger">
                                    <div className="fw-bold mb-1">Could not be sent:</div>
                                    <ul className="mb-0 ps-3">
                                        {failedRows.map((row: any) => (
                                            <li key={row.layer_id}>
                                                {pallets.find((p) => p.id === row.layer_id)?.label ?? row.layer_id}
                                                : {row.detail}
                                            </li>
                                        ))}
                                    </ul>
                                </Alert>
                            )}

                            {skippedRows.length > 0 && (
                                <Alert color="warning" className="mb-0">
                                    {skippedRows.length} pallet{skippedRows.length === 1 ? " was" : "s were"} skipped
                                    because no external integration is configured for them.
                                </Alert>
                            )}
                        </>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button color="secondary" outline onClick={close} disabled={isSending}>
                        Close
                    </Button>

                    <Button
                        color="primary"
                        onClick={handleSend}
                        disabled={isSending || selected.length === 0 || tooMany}
                        isLoading={isSending}
                        icon={faPaperPlane}
                    >
                        {isSending ? "Sending..." : `Send ${selected.length} pallet${selected.length === 1 ? "" : "s"}`}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}
